/**
 * useCountryCatalog — SQL-backed ISO 3166-1 country catalog (Bill ruling #3,
 * 2026-06-12, approved build).
 *
 * Fetches GET /v3/config/countries ONCE per session (module-level cache —
 * concurrent consumers share one request, same pattern as
 * useCoreDirectorCodes) and FALLS BACK to the legacy 80-entry
 * COUNTRIES_OF_CITIZENSHIP list mapped into the same shape while loading or
 * on failure, so dropdowns always render.
 *
 * Server side: dbo.CountryCatalog (migration 20260807000000_V3_CountryCatalog
 * + seed 94-country-catalog-iso3166.sql — all 249 ISO 3166-1 entries).
 *
 * Core Director destinations this catalog feeds (documented for the
 * commit-pipeline follow-up — CD writes are NOT wired from this hook):
 *   F949 Country Code (2-character)  ← cdCode (seeded = iso2)
 *   F355 Country of Birth / F356 Citizenship Country / F357 Dual Citizenship
 *   F23  Call Class = 3 Foreign/Non-US Citizen
 *   F256 CIP ID Issuer (40 alphanumeric) ← name — per Bill 2026-06-12,
 *        spot-checked existing NRA CIFs carry the FULL country name ("India").
 *
 * This module is ALSO the FE's one country-normalization util
 * (resolveCountryEntry / resolveCountryIso2 / resolveCountryName): DI/CU
 * analyzers emit three shapes — alpha-2 ("IN"), alpha-3 ("IND", passport
 * MRZ), and full names ("INDIA", the I-20 prints the name) — and all three
 * resolve through the catalog (fetched data when available, the static
 * fallback otherwise). captureRouting.citizenshipToIso2 delegates here.
 */

import { useEffect, useState } from 'react';
import { useApiClient } from '@/contexts/ApiClientContext';
import { normalizeIso2 } from '../../../utils/isoCountry.js';
import { COUNTRIES_OF_CITIZENSHIP } from './individual-config.js';

/** One catalog entry — the cross-lane hook contract shape. */
export interface CountryCatalogEntry {
  readonly name: string;
  readonly iso2: string;
  readonly iso3: string;
  readonly cdCode: string;
}

/** Wire row from GET /v3/config/countries (camelCase server projection). */
interface CountryWireRow {
  readonly name: string;
  readonly iso2: string;
  readonly iso3: string;
  readonly numericCode: string | null;
  readonly cdCountryCode: string;
  readonly specialRequirements: string | null;
  readonly isActive: boolean;
  readonly sortOrder: number | null;
}

/**
 * Static fallback — the legacy 80-entry list mapped into the catalog shape.
 * iso3 is unknown on the legacy rows (left ''); resolvers convert alpha-3
 * input through normalizeIso2 first so fallback matching still works.
 * cdCode = iso2 (the seed default — F949 is 2-character).
 */
const FALLBACK_COUNTRIES: ReadonlyArray<CountryCatalogEntry> =
  COUNTRIES_OF_CITIZENSHIP.map((c) => ({
    name: c.label,
    iso2: c.code,
    iso3: '',
    cdCode: c.code,
  }));

// ── module cache (one fetch per session; resolvers read it synchronously) ───
let cachedCountries: ReadonlyArray<CountryCatalogEntry> | null = null;
let cachedPromise: Promise<ReadonlyArray<CountryCatalogEntry> | null> | null = null;

function loadCatalog(
  client: ReturnType<typeof useApiClient>,
): Promise<ReadonlyArray<CountryCatalogEntry> | null> {
  if (cachedCountries !== null) return Promise.resolve(cachedCountries);
  if (cachedPromise !== null) return cachedPromise;
  cachedPromise = client.http
    .get<{ success: boolean; data?: ReadonlyArray<CountryWireRow>; error?: string }>(
      '/v3/config/countries',
    )
    .then((resp) => {
      if (resp.success && resp.data && resp.data.length > 0) {
        cachedCountries = resp.data.map((r) => ({
          name: r.name,
          iso2: r.iso2,
          iso3: r.iso3,
          cdCode: r.cdCountryCode,
        }));
        return cachedCountries;
      }
      // Empty/odd payload — allow a later retry rather than caching nothing.
      cachedPromise = null;
      return null;
    })
    .catch(() => {
      // 503 COUNTRY_CATALOG_UNAVAILABLE (pre-migration) or network failure —
      // consumers keep the static fallback; next mount retries.
      cachedPromise = null;
      return null;
    });
  return cachedPromise;
}

/**
 * Country catalog hook — EXACT cross-lane contract:
 *   useCountryCatalog(): {
 *     countries: ReadonlyArray<{ name; iso2; iso3; cdCode }>,
 *     loading: boolean,
 *   }
 * `countries` is the fetched catalog once loaded; the legacy fallback (same
 * shape) while loading or when the endpoint is unavailable.
 */
export function useCountryCatalog(): {
  countries: ReadonlyArray<{ name: string; iso2: string; iso3: string; cdCode: string }>;
  loading: boolean;
} {
  const client = useApiClient();
  const [, setVersion] = useState(0);
  const [loading, setLoading] = useState(cachedCountries === null);

  useEffect(() => {
    if (cachedCountries !== null) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    void loadCatalog(client).then(() => {
      if (cancelled) return;
      setLoading(false);
      setVersion((v) => v + 1); // re-render with the fetched catalog
    });
    return () => {
      cancelled = true;
    };
  }, [client]);

  return {
    countries: cachedCountries ?? FALLBACK_COUNTRIES,
    loading: loading && cachedCountries === null,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Normalization util — ONE resolver for every DI/CU country shape.
// Synchronous on purpose: capture-routing mappers are plain functions. They
// read the module cache (populated by the hook on dialog mount) and fall back
// to the static list, so resolution always returns something sensible.
// ─────────────────────────────────────────────────────────────────────────────

/** Catalog rows the resolvers operate on right now (fetched or fallback). */
function activeCatalog(): ReadonlyArray<CountryCatalogEntry> {
  return cachedCountries ?? FALLBACK_COUNTRIES;
}

/**
 * Resolve any analyzer-emitted country value to a catalog entry, or null.
 * Accepts alpha-2 ("IN"), alpha-3 ("IND" — converted via normalizeIso2 and
 * matched against catalog iso3 when present), full names ("INDIA"), and
 * UNIQUE name prefixes of ≥4 chars (visa foils print truncations like "JORD"
 * for Jordan — live CU corpus). Ambiguous prefixes resolve to nothing rather
 * than guessing.
 */
export function resolveCountryEntry(raw: string | null | undefined): CountryCatalogEntry | null {
  if (!raw) return null;
  const needle = raw.trim();
  if (needle.length === 0) return null;
  const up = needle.toUpperCase();
  const catalog = activeCatalog();

  // 1. alpha-2 exact.
  if (up.length === 2) {
    return catalog.find((c) => c.iso2.toUpperCase() === up) ?? null;
  }

  // 2. alpha-3 — catalog iso3 first (real ISO data when fetched), then the
  //    static alpha-3→alpha-2 bridge for the legacy fallback rows (iso3='').
  if (up.length === 3) {
    const byIso3 = catalog.find((c) => c.iso3.toUpperCase() === up);
    if (byIso3) return byIso3;
    const viaIso2 = normalizeIso2(up);
    if (viaIso2.length === 2) {
      const bridged = catalog.find((c) => c.iso2.toUpperCase() === viaIso2);
      if (bridged) return bridged;
    }
  }

  // 3. Full-name exact (the I-20 prints "INDIA").
  const byName = catalog.find((c) => c.name.toUpperCase() === up);
  if (byName) return byName;

  // 4. UNIQUE name prefix, ≥4 chars ("JORD" → Jordan). Two+ hits → null.
  if (up.length >= 4) {
    const hits = catalog.filter((c) => c.name.toUpperCase().startsWith(up));
    if (hits.length === 1) return hits[0] ?? null;
  }

  return null;
}

/**
 * Country value → ISO alpha-2 for the CountrySelect dropdowns. Resolves
 * through the catalog; falls through to normalizeIso2's behavior (upper-cased
 * input) when nothing matches, so a non-empty input never becomes empty.
 *
 * Behavior (catalog loaded): "IND" → "IN" · "IN" → "IN" · "INDIA" → "IN" ·
 * "JORD" → "JO" · unknown "XX…" → upper-cased passthrough.
 */
export function resolveCountryIso2(raw: string): string {
  const entry = resolveCountryEntry(raw);
  if (entry) return entry.iso2.toUpperCase();
  return normalizeIso2(raw);
}

/**
 * Country value → FULL country name, or null when unresolvable.
 * The F256 CIP ID Issuer write shape for foreign passports (Bill 2026-06-12
 * ruling: F256 = 40 alphanumeric; existing NRA CIFs carry the full name,
 * e.g. "India" — never the raw "IND"/"IN" code).
 */
export function resolveCountryName(raw: string | null | undefined): string | null {
  return resolveCountryEntry(raw)?.name ?? null;
}
